"use client";

import { useEffect, useRef } from "react";

export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const stateRef = useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
    movedDistance: 0,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      stateRef.current = {
        isDown: true,
        startX: e.pageX,
        startScrollLeft: el.scrollLeft,
        movedDistance: 0,
      };
      el.style.cursor = "grabbing";
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!stateRef.current.isDown) return;
      e.preventDefault();
      const dx = e.pageX - stateRef.current.startX;
      stateRef.current.movedDistance = Math.max(
        stateRef.current.movedDistance,
        Math.abs(dx),
      );
      el.scrollLeft = stateRef.current.startScrollLeft - dx;
    };

    const handleMouseUp = () => {
      if (!stateRef.current.isDown) return;
      stateRef.current.isDown = false;
      el.style.cursor = "";
    };

    el.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const didDrag = (threshold = 5) =>
    stateRef.current.movedDistance > threshold;

  return { ref, didDrag };
}
